import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  FormHelperText,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import { isValidPhoneNumber, parsePhoneNumber } from '../../utils/validation';

// Country codes for the dropdown with flag emojis
const countryCodes = [
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+1', country: 'US/CA', flag: '🇺🇸/🇨🇦' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  fullWidth?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  label = 'Phone Number',
  required = false,
  fullWidth = true,
}) => {
  const [countryCode, setCountryCode] = useState<string>('+44');
  const [nationalNumber, setNationalNumber] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true);

  // Parse the initial value to extract country code and national number
  useEffect(() => {
    if (value) {
      const { countryCode: parsedCode, nationalNumber: parsedNumber } = parsePhoneNumber(value);
      setCountryCode(parsedCode);
      setNationalNumber(parsedNumber);
    }
  }, []);

  // Combine country code and national number to form the full phone number
  const handleCountryCodeChange = (event: SelectChangeEvent<string>) => {
    const newCountryCode = event.target.value;
    setCountryCode(newCountryCode);
    const fullNumber = `${newCountryCode} ${nationalNumber}`;
    onChange(fullNumber);
    setIsValid(isValidPhoneNumber(fullNumber));
  };

  const handleNationalNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNationalNumber = event.target.value;
    setNationalNumber(newNationalNumber);
    const fullNumber = `${countryCode} ${newNationalNumber}`;
    onChange(fullNumber);
    setIsValid(isValidPhoneNumber(fullNumber));
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Select
          value={countryCode}
          onChange={handleCountryCodeChange}
          disabled={disabled}
          error={error || !isValid}
          variant="outlined"
          sx={{ 
            width: '30%', 
            minWidth: '100px',
            height: '56px'
          }}
          renderValue={(selected) => {
            const country = countryCodes.find(c => c.code === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.2em' }}>{country?.flag}</span>
                <span>{selected}</span>
              </Box>
            );
          }}
        >
          {countryCodes.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.2em' }}>{country.flag}</span>
                <span>{country.code}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
        <TextField
          label={label}
          value={nationalNumber}
          onChange={handleNationalNumberChange}
          disabled={disabled}
          error={error || !isValid}
          required={required}
          fullWidth
          placeholder="123 456 7890"
          variant="outlined"
          sx={{ height: '56px' }}
          helperText={(helperText || !isValid) ? (!isValid ? 'Please enter a valid phone number' : helperText) : undefined}
        />
      </Box>
    </Box>
  );
};

export default PhoneInput;
